/**
 * Irmão: `src/app/components/filters/FilterChipGroup.tsx`.
 *
 * Duplicação deliberada (openspec/changes/flashcards-revisao-redesign/design.md,
 * Decisão 2). Não reusar `components/filters/`: store, persistência e efeito de
 * aplicação são de outra tela. Uma correção visual aqui deve ser conferida
 * no irmão, e o inverso.
 */
import { KeyboardEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { revisaoFilterCx } from "./tokens";

export interface RevisaoFilterChipGroupProps {
  children: ReactNode;
  /** id of the element naming this group — usually the section title. */
  labelledBy?: string;
  ariaLabel?: string;
  /** Single-choice groups announce themselves as a radiogroup. */
  role?: "group" | "radiogroup";
  className?: string;
}

/**
 * Chip grid with roving tabindex.
 *
 * A section can hold fifty options; without this, Tab walks every one of them
 * before reaching the apply button (§23.16). Exactly one chip is tabbable and
 * the arrow keys move between them, which is the expected behaviour for a
 * composite widget.
 */
export default function RevisaoFilterChipGroup({
  children,
  labelledBy,
  ariaLabel,
  role = "group",
  className,
}: RevisaoFilterChipGroupProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const chips = useCallback((): HTMLButtonElement[] => {
    const root = containerRef.current;
    if (!root) return [];
    return Array.from(
      root.querySelectorAll<HTMLButtonElement>("button[data-chip]:not([disabled])"),
    );
  }, []);

  // Runs on every render on purpose: the option list changes as the user
  // searches or expands, and the tabbable chip has to follow it.
  useEffect(() => {
    const list = chips();
    if (list.length === 0) return;
    const index = Math.min(activeIndex, list.length - 1);
    list.forEach((chip, i) => {
      chip.tabIndex = i === index ? 0 : -1;
    });
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const list = chips();
    if (list.length === 0) return;

    const current = list.indexOf(document.activeElement as HTMLButtonElement);
    if (current === -1) return;

    let next: number | null = null;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = (current + 1) % list.length;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = (current - 1 + list.length) % list.length;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = list.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    setActiveIndex(next);
    list[next].focus();
  };

  return (
    <div
      ref={containerRef}
      role={role}
      aria-labelledby={labelledBy}
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={revisaoFilterCx("flex flex-wrap gap-2", className)}
    >
      {children}
    </div>
  );
}
